// Builder ⇄ backend serialization for the form design.
//
// Save (buildElementsPayload) — flattens the builder's nested pages state into
// the single elements array PATCH /forms/update-elements expects. Numbering
// per the confirmed contract: `position` is the global 1-based index across
// the flat array; a page element carries its own 1-based `pageNumber` and
// every row after it (until the next page) shares it. `sectionNumber` follows
// MEMBERSHIP, not position: a section row carries its own 1-based ordinal
// (restarting per page), a field row carries its owning section's ordinal
// (tracked via the field's client `sectionId`), and 0 everywhere else — page
// rows, fields before any section, and top-level fields sitting AFTER a
// section (confirmed possible). `properties` is JSON-stringified; the
// client-only `id`/`sectionId` live outside `properties`, so nothing is
// stripped. `_id`/
// `elementGlobalId` always go on the wire — the real ids on elements seeded from
// a saved design, explicit nulls on builder-created ones;
// never-saved FIELD rows instead carry `uniqueIdToReplace` (a temp wire id set
// when the element is created), so a dataSource binding made before the first
// save — bindings reference `elementGlobalId ?? uniqueIdToReplace` — still
// points at something the backend can rewrite. Page rows never carry it (no
// dataSource binding references a page) — but every page row DOES carry
// `triggerFlowReferenceId`, the uuid a `user_page` trigger action targets when
// the page has never been saved.
//
// Conditional logic (serializeTriggers) — a field's client rules (one action
// each) group onto the wire as `triggers: { is: { [optionValue]: entries[] } }`:
// same-answer rules merge into one entry array, incomplete rules (no answer or
// no action) and rules whose references no longer resolve are silently dropped
// (legacy-exact), and the key is OMITTED — never an empty object — when nothing
// survives. ask_a_question follow-up rows are ordinary element rows carrying
// their own `triggerFlowReferenceId` (the same uuid as the parent's entry, how
// the backend links them); the seed's second pass re-splits grouped entries
// into one rule per action and resolves references back to client ids via
// elementGlobalId.
//
// Load (seedPagesFromDesign) — the inverse: walks the detail's position-sorted
// design elements, `page` rows start a new BuilderPage and everything else
// becomes a field on the current page; a field's `sectionNumber` maps back to
// the client `sectionId` of that page's Nth section row (0 → top-level). Parsed properties are
// fitted into the state interface (fitProperties): only the keys the type's
// defaults define are copied over, so unknown backend keys (legacy fields like
// dbColumnIdentifier) are ignored and never round-trip, and missing keys keep
// their defaults.
//
// Image fields (uploadPendingFieldImages + serializeProperties) — a picked file
// sits in `basicProperties.fieldImageFile` and is uploaded only when the user
// saves: reserve a presigned URL per file (POST media/form-media), PUT the
// bytes, then store `fieldImage = {formId, versionId, id: mediaId}`. The File
// itself never reaches the wire, so only a live File triggers an upload —
// re-saving an unchanged image uploads nothing. No display URL is persisted or
// even kept in the design state: useFieldImageUrl derives it at render time.

import { v4 as uuidv4 } from 'uuid';
import type {
  FormDesignElement,
  FormElementPayload,
  FormElementTriggers,
  FormTriggerTypeEntry,
} from '@/api/endpoints/formsApi';
import type { FormMediaInput, FormMediaResult } from '@/api/endpoints/mediaApi';
import { uploadToPresignedUrl } from '@/utils/uploadFile';
import type { BuilderField, BuilderPage, BuilderRule } from './builderModel';
import {
  createElementProperties,
  formElementDefaultProperties,
  type ElementProperties,
  type ElementType,
  type FieldImageRef,
  type LibraryElementType,
  type PageProperties,
} from './formElementDefaults';

// Stringify an element's properties for the wire. An image field's
// `fieldImageFile` is client-only — a pending File that would stringify to `{}`
// — so it never goes out; `fieldImage` is whitelisted down to the three keys
// that identify the image, so no client-side addition can ever leak into the
// saved design.
const serializeProperties = (properties: ElementProperties): string => {
  const groups = properties as unknown as Record<string, Record<string, unknown>>;
  const basic = groups.basicProperties;
  if (!basic || !('fieldImage' in basic || 'fieldImageFile' in basic)) {
    return JSON.stringify(properties);
  }

  const ref = basic.fieldImage as FieldImageRef | null;
  const basicOut: Record<string, unknown> = { ...basic };
  delete basicOut.fieldImageFile;
  basicOut.fieldImage = ref ? { formId: ref.formId, versionId: ref.versionId, id: ref.id } : null;
  return JSON.stringify({ ...groups, basicProperties: basicOut });
};

/** The field's option VALUES (checkList; [] for types without an optionList). */
const optionValuesOf = (field: BuilderField): string[] => {
  const basic = (field.properties as unknown as Record<string, Record<string, unknown>>)
    .basicProperties;
  const list = basic?.optionList;
  return Array.isArray(list) ? list.map((o) => String((o as { value?: unknown }).value ?? '')) : [];
};

/** Group a field's complete rules for the wire, or undefined when none survive
 *  (the `triggers` key must then be OMITTED, matching the legacy serializer).
 *  Dropped silently, legacy-exact: incomplete rules (no answer / no action),
 *  rules whose answer value no longer exists in the optionList (cascades keep
 *  this from happening — belt and braces), and rules whose referenced child
 *  field or target page no longer resolves. */
const serializeTriggers = (
  field: BuilderField,
  fieldById: Map<string, BuilderField>,
  pageById: Map<string, BuilderPage>,
): FormElementTriggers | undefined => {
  if (!field.rules.length) return undefined;
  const values = new Set(optionValuesOf(field));

  const grouped: Record<string, FormTriggerTypeEntry[]> = {};
  const entryOf = (rule: BuilderRule): FormTriggerTypeEntry | null => {
    const action = rule.action;
    if (!action) return null;
    if (action.kind === 'ask_a_question') {
      const child = fieldById.get(action.childId);
      if (!child) return null;
      return {
        type: 'ask_a_question',
        elementGlobalId: child.elementGlobalId,
        triggerFlowReferenceId: child.triggerFlowReferenceId,
      };
    }
    if (action.kind === 'user_page') {
      const page = pageById.get(action.pageId);
      if (!page) return null;
      return {
        type: 'user_page',
        elementGlobalId: page.elementGlobalId,
        triggerFlowReferenceId: page.triggerFlowReferenceId,
      };
    }
    return { type: action.kind };
  };

  for (const rule of field.rules) {
    if (!rule.matchingValue || !values.has(rule.matchingValue)) continue;
    const entry = entryOf(rule);
    if (!entry) continue;
    (grouped[rule.matchingValue] ??= []).push(entry);
  }

  // The new UI only ever writes the `is` matcher (product decision).
  return Object.keys(grouped).length ? { is: grouped } : undefined;
};

export const buildElementsPayload = (pages: BuilderPage[]): FormElementPayload[] => {
  const elements: FormElementPayload[] = [];
  // Rule references are CLIENT ids; the wire ids are looked up here, at save
  // time, so they are always the freshest the model holds.
  const fieldById = new Map<string, BuilderField>();
  const pageById = new Map<string, BuilderPage>();
  for (const page of pages) {
    pageById.set(page.id, page);
    for (const field of page.fields) fieldById.set(field.id, field);
  }

  // Rows that actually go on the wire. Ordinary rows always do; a follow-up
  // row goes only while a COMPLETE rule on an emitted row references it — an
  // orphaned follow-up (its rule incomplete, or its owner itself orphaned)
  // must not leak into the design as an ordinary question. The UI keeps this
  // from happening (actions exist only behind a picked answer); this guard
  // makes the invariant a property of the payload, not of the UI. Depth ≤ 2,
  // so the closure settles in two passes.
  const emittable = new Set<string>();
  for (const field of fieldById.values()) {
    if (field.triggerParentId === null) emittable.add(field.id);
  }
  let grew = true;
  while (grew) {
    grew = false;
    for (const field of fieldById.values()) {
      if (!emittable.has(field.id)) continue;
      const values = new Set(optionValuesOf(field));
      for (const rule of field.rules) {
        if (!rule.matchingValue || !values.has(rule.matchingValue)) continue;
        if (rule.action?.kind !== 'ask_a_question') continue;
        if (fieldById.has(rule.action.childId) && !emittable.has(rule.action.childId)) {
          emittable.add(rule.action.childId);
          grew = true;
        }
      }
    }
  }

  pages.forEach((page, pageIndex) => {
    const pageNumber = pageIndex + 1;
    // Ordinal of the last section row seen on this page, and its client id —
    // a field row gets the ordinal only if its sectionId says it lives there.
    let sectionOrdinal = 0;
    let currentSectionId: string | null = null;

    elements.push({
      // No `uniqueIdToReplace` on page rows — by design, pages are never
      // referenced by dataSource bindings (see BuilderPage). user_page trigger
      // actions instead target the page's triggerFlowReferenceId, sent always.
      _id: page._id,
      elementGlobalId: page.elementGlobalId,
      elementType: page.elementType,
      position: elements.length + 1,
      pageNumber,
      sectionNumber: 0,
      properties: serializeProperties(page.properties),
      triggerFlowReferenceId: page.triggerFlowReferenceId,
    });

    for (const field of page.fields) {
      // Orphaned follow-up — nothing references it, so it never goes out.
      if (field.triggerParentId !== null && !emittable.has(field.id)) continue;
      // A section row starts its own section — it carries the new number.
      if (field.elementType === 'section') {
        sectionOrdinal += 1;
        currentSectionId = field.id;
      }
      const owned = field.sectionId !== null && field.sectionId === currentSectionId;
      const triggers = serializeTriggers(field, fieldById, pageById);
      elements.push({
        _id: field._id,
        elementGlobalId: field.elementGlobalId,
        // Set only on never-saved elements (see BuilderField) — it is what
        // lets the backend rewrite dataSource binding references once it
        // assigns the real elementGlobalId.
        uniqueIdToReplace: field.uniqueIdToReplace,
        elementType: field.elementType,
        position: elements.length + 1,
        pageNumber,
        // 0 = a top-level field (possibly AFTER a section — membership is the
        // field's sectionId, never its position).
        sectionNumber: field.elementType === 'section' || owned ? sectionOrdinal : 0,
        properties: serializeProperties(field.properties),
        // Both OMITTED (not nulled) when absent, matching the legacy payload.
        ...(triggers ? { triggers } : {}),
        ...(field.triggerFlowReferenceId
          ? { triggerFlowReferenceId: field.triggerFlowReferenceId }
          : {}),
      });
    }
  });

  return elements;
};

// Fit parsed backend properties into the state interface: start from the
// type's defaults and copy over only the group/key pairs the defaults define —
// unknown backend keys are ignored, missing ones keep their default (incl. the
// freshly stamped fieldUniqueId, overwritten when the backend carries one).
const fitProperties = (type: ElementType, raw: unknown): ElementProperties => {
  const fitted = createElementProperties(type) as unknown as Record<
    string,
    Record<string, unknown>
  >;
  if (raw && typeof raw === 'object') {
    const parsed = raw as Record<string, unknown>;
    for (const group of Object.keys(fitted)) {
      const parsedGroup = parsed[group];
      if (!parsedGroup || typeof parsedGroup !== 'object') continue;
      for (const key of Object.keys(fitted[group])) {
        if (key in parsedGroup) fitted[group][key] = (parsedGroup as Record<string, unknown>)[key];
      }
    }
  }
  return fitted as unknown as ElementProperties;
};

// Parse a saved element's properties JSON and fit it into the state shape;
// bad/missing JSON falls back to the type's pristine defaults.
const parseProperties = (element: FormDesignElement, type: ElementType): ElementProperties => {
  try {
    return fitProperties(type, JSON.parse(element.properties));
  } catch {
    return createElementProperties(type);
  }
};

/** A never-saved blank page — explicit null wire ids, type-default properties. */
export const createBlankPage = (): BuilderPage => ({
  id: uuidv4(),
  _id: null,
  elementGlobalId: null,
  elementType: 'page',
  properties: createElementProperties('page'),
  fields: [],
  // What a user_page trigger action targets while the page is never-saved.
  triggerFlowReferenceId: uuidv4(),
});

/** A never-saved field as the library adds it. `sectionId` names the section it
 *  joins; sections themselves are always top-level. */
export const createLibraryField = (
  type: LibraryElementType,
  sectionId: string | null = null,
): BuilderField => {
  // One identity for a never-saved element: the client uuid also travels as
  // `uniqueIdToReplace`, so bindings resolve before the first save.
  const id = uuidv4();
  return {
    id,
    _id: null,
    elementGlobalId: null,
    uniqueIdToReplace: id,
    sectionId: type === 'section' ? null : sectionId,
    elementType: type,
    properties: createElementProperties(type),
    collapsed: false,
    rules: [],
    // Library adds are never follow-ups — ask_a_question children are
    // created by the logic drawer, which stamps both of these.
    triggerFlowReferenceId: null,
    triggerParentId: null,
  };
};

/** The starting canvas for a form with no saved elements: one page holding the
 *  form's exactly-once required companyStructure field. Titles stay at their
 *  blank defaults — the admin names both before Save validates. */
export const createDefaultPages = (): BuilderPage[] => [
  { ...createBlankPage(), fields: [createLibraryField('companyStructure')] },
];

/** Rebuild the builder's pages state from a saved design's (position-sorted)
 *  elements. Unknown element types (legacy app fields this builder doesn't
 *  offer) are skipped — they'd crash the defaults-driven properties panel.
 *  Returns [] when nothing rebuildable is saved (caller keeps its blank page).
 *
 *  Two passes: pass 1 builds every page and field (a `user_page` action may
 *  target a LATER page, and an ask_a_question entry references its child by
 *  elementGlobalId, so no reference can be resolved until everything exists);
 *  pass 2 ungroups each element's saved `triggers` into client rules — one
 *  rule per action entry (the drawer shows one action per rule) — resolving
 *  wire ids back to client ids. A rule whose reference doesn't resolve (e.g.
 *  its child row was an unknown legacy type and got skipped) is dropped. */
export const seedPagesFromDesign = (elements: FormDesignElement[]): BuilderPage[] => {
  const pages: BuilderPage[] = [];
  // Client ids of the current page's section rows, in order — a field's saved
  // sectionNumber indexes into this (1-based; 0 = top-level, even after a
  // section). Reset on every new page.
  let sectionIds: string[] = [];

  // Pass-2 inputs: fields whose rows carried triggers, and wire-id indexes.
  const withTriggers: { field: BuilderField; triggers: FormElementTriggers }[] = [];
  const fieldByGlobalId = new Map<string, BuilderField>();
  const pageByWireId = new Map<string, BuilderPage>();

  for (const element of elements) {
    if (!(element.elementType in formElementDefaultProperties)) continue;
    const type = element.elementType as ElementType;

    if (type === 'page') {
      const page: BuilderPage = {
        id: uuidv4(),
        _id: element._id,
        elementGlobalId: element.elementGlobalId,
        elementType: 'page',
        properties: parseProperties(element, 'page') as PageProperties,
        fields: [],
        // Saved page — its stable wire identity IS the elementGlobalId
        // (legacy parity: user_page entries reference elementGlobalId ?? refId).
        triggerFlowReferenceId: element.elementGlobalId,
      };
      pages.push(page);
      pageByWireId.set(element.elementGlobalId, page);
      if (element.triggerFlowReferenceId) pageByWireId.set(element.triggerFlowReferenceId, page);
      sectionIds = [];
      continue;
    }

    // Defensive: a field before any page row gets an implicit blank page.
    if (!pages.length) {
      pages.push(createBlankPage());
      sectionIds = [];
    }

    const field: BuilderField = {
      id: uuidv4(),
      _id: element._id,
      elementGlobalId: element.elementGlobalId,
      // Saved element — it has a real elementGlobalId, so nothing to replace.
      uniqueIdToReplace: undefined,
      sectionId: type === 'section' ? null : (sectionIds[element.sectionNumber - 1] ?? null),
      elementType: type as LibraryElementType,
      properties: parseProperties(element, type),
      collapsed: false,
      rules: [],
      // Overwritten in pass 2 for rows an ask_a_question entry references.
      triggerFlowReferenceId: null,
      triggerParentId: null,
    };
    if (type === 'section') sectionIds.push(field.id);
    fieldByGlobalId.set(element.elementGlobalId, field);
    if (element.triggers && Object.keys(element.triggers).length) {
      withTriggers.push({ field, triggers: element.triggers });
    }
    pages[pages.length - 1].fields.push(field);
  }

  // Pass 2 — resolve references. Only the `is` matcher is processed: the new
  // UI writes nothing else and no legacy-authored forms exist (confirmed).
  for (const { field, triggers } of withTriggers) {
    const byValue = triggers.is;
    if (!byValue) continue;
    for (const [matchingValue, entries] of Object.entries(byValue)) {
      for (const entry of entries) {
        if (entry.type === 'ask_a_question') {
          const child = entry.elementGlobalId
            ? fieldByGlobalId.get(entry.elementGlobalId)
            : undefined;
          if (!child) continue;
          child.triggerParentId = field.id;
          child.triggerFlowReferenceId = child.elementGlobalId;
          field.rules.push({
            id: uuidv4(),
            matchingValue,
            action: { kind: 'ask_a_question', childId: child.id },
          });
        } else if (entry.type === 'user_page') {
          const wireId = entry.elementGlobalId ?? entry.triggerFlowReferenceId;
          const page = wireId ? pageByWireId.get(wireId) : undefined;
          if (!page) continue;
          field.rules.push({
            id: uuidv4(),
            matchingValue,
            action: { kind: 'user_page', pageId: page.id },
          });
        } else if (
          entry.type === 'require_picture' ||
          entry.type === 'require_video' ||
          entry.type === 'require_note'
        ) {
          field.rules.push({ id: uuidv4(), matchingValue, action: { kind: entry.type } });
        }
      }
    }
  }

  // Pass 3 — rule order. The grouped wire object loses it (JS iterates
  // integer-like keys numerically, so pass 2 rebuilt rules value-sorted), but
  // ask children are real rows whose POSITION preserved it: sort each field's
  // rules the way the canvas shows their follow-ups. Bare rules own no row —
  // they follow, in option order (the only deterministic order left). Stable
  // sort, so equal ranks keep their pass-2 relative order.
  for (const page of pages) {
    const flatIndex = new Map(page.fields.map((f, i) => [f.id, i]));
    for (const field of page.fields) {
      if (field.rules.length < 2) continue;
      const values = optionValuesOf(field);
      const rank = (rule: BuilderRule): number => {
        if (rule.action?.kind === 'ask_a_question') {
          return flatIndex.get(rule.action.childId) ?? page.fields.length;
        }
        const i = values.indexOf(rule.matchingValue);
        return page.fields.length + 1 + (i < 0 ? values.length : i);
      };
      field.rules.sort((a, b) => rank(a) - rank(b));
    }
  }

  return pages;
};

/** Reserve + upload, injected by the caller (RTK Query's `formMedia` trigger) so
 *  this module stays free of API/hook imports. */
type ReserveMedia = (input: FormMediaInput) => { unwrap: () => Promise<FormMediaResult> };

interface UploadContext {
  formId: string;
  versionId: string;
  reserve: ReserveMedia;
}

/** Upload every image field that holds a freshly picked `File`, and return the
 *  pages with each one's descriptor filled in (`fieldImageFile` cleared, the
 *  in-session object URL kept so the preview doesn't flicker). Called on save
 *  only — picking a file never touches the network. Concurrent; any failure
 *  rejects, so the caller aborts the save before PATCHing. Returns the SAME
 *  array reference when nothing was pending, so callers can skip a state write. */
export const uploadPendingFieldImages = async (
  pages: BuilderPage[],
  { formId, versionId, reserve }: UploadContext,
): Promise<BuilderPage[]> => {
  const pending: BuilderField[] = [];
  for (const page of pages) {
    for (const field of page.fields) {
      const basic = (field.properties as unknown as Record<string, Record<string, unknown>>)
        .basicProperties;
      if (basic?.fieldImageFile instanceof File) pending.push(field);
    }
  }
  if (!pending.length) return pages;

  // mediaId per pending field, uploaded in parallel.
  const uploaded = new Map<string, string>();
  await Promise.all(
    pending.map(async (field) => {
      const basic = (field.properties as unknown as Record<string, Record<string, unknown>>)
        .basicProperties;
      const file = basic.fieldImageFile as File;
      const ext = file.name.split('.').pop() || 'png';
      const { uploadUrl, mediaId } = await reserve({
        mediaType: 'image',
        imageName: `${Date.now()}.${ext}`,
        formId,
        versionId,
      }).unwrap();
      await uploadToPresignedUrl(uploadUrl, file);
      uploaded.set(field.id, mediaId);
    }),
  );

  return pages.map((page) => ({
    ...page,
    fields: page.fields.map((field) => {
      const mediaId = uploaded.get(field.id);
      if (!mediaId) return field;
      const groups = field.properties as unknown as Record<string, Record<string, unknown>>;
      return {
        ...field,
        properties: {
          ...groups,
          basicProperties: {
            ...groups.basicProperties,
            // The File is spent; from here the image is identified by its id and
            // the display URL is fetched at render time.
            fieldImageFile: null,
            fieldImage: { formId, versionId, id: mediaId },
          },
        } as unknown as ElementProperties,
      };
    }),
  }));
};
